import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  DialogContentText,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { useWebSocket, useUser } from '../WebSocketContext';
import { useNavigate } from 'react-router-dom';
import WidgetsIcon from '@mui/icons-material/Widgets';
import TagsDropdown from '../TagsDropdown';
import TagEditor from './TagEditor';
import TagChip from '../Common/TagChip';
import CloseButton from '../Common/CloseButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { parseVisibilityFlag } from './widgetVisibilityUtils.js';
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';

const tagChipBaseStyle = {
  mt: 0.5,
  backgroundColor: 'rgba(87, 87, 176, 0.12)',
  color: primaryColor,
  fontSize: '0.75rem',
  '& .MuiChip-label': { px: 1 }
};

const headerChipStyle = {
  mt: 0.5,
  backgroundColor: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: '0.75rem',
  '& .MuiChip-label': { px: 1 }
};

const normalizeHeaderColor = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return /^#([0-9a-fA-F]{3}){1,2}$/.test(trimmed) ? trimmed : null;
};

const AddWidgetModal = ({ open, onClose, caseid, editingWidget = null, initialTagName = '', onWidgetOptimisticUpdate }) => {
  const { t } = useTranslation();
  const { ws, sendMessage, subscribeToMessages } = useWebSocket();
  const user = useUser();
  const isReadOnly = user?.isReadOnly;
  // navigate is declared below after state hooks are initialized

  // Determine if we're in edit mode
  const isEditMode = !!editingWidget;
  const defaultHeaderColor = primaryColor;

  // Form state
  const [title, setTitle] = useState('');
  const [tagName, setTagName] = useState('');
  const [editingTagName, setEditingTagName] = useState(false);
  const [userEditedTag, setUserEditedTag] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [descriptionTagsAnchor, setDescriptionTagsAnchor] = useState(null);
  const [useTimeevents, setUseTimeevents] = useState(true);
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [globalHeaderColor, setGlobalHeaderColor] = useState(defaultHeaderColor);
  const [initialGlobalHeaderColor, setInitialGlobalHeaderColor] = useState(null);
  const [globalHeaderColorCleared, setGlobalHeaderColorCleared] = useState(false);
  const [headerColor, setHeaderColor] = useState(defaultHeaderColor);
  const [initialHeaderColor, setInitialHeaderColor] = useState(null);
  const [headerColorCleared, setHeaderColorCleared] = useState(false);
  const [initialTitle, setInitialTitle] = useState('');
  const [initialTagNameValue, setInitialTagNameValue] = useState('');
  const [initialInstruction, setInitialInstruction] = useState('');
  const [descriptionChangeDialogOpen, setDescriptionChangeDialogOpen] = useState(false);
  const [discardChangesDialogOpen, setDiscardChangesDialogOpen] = useState(false);
  const [placeholderInfoOpen, setPlaceholderInfoOpen] = useState(false);
  const [initialIsPlaceholder, setInitialIsPlaceholder] = useState(false);
  // copiedTag UI removed; keep copy handler but no local state
  const instructionRef = useRef(null);
  const skipCloseOnUpdateRef = useRef(false);
  const navigate = useNavigate();

  // Pre-populate form when editing
  useEffect(() => {
    if (isEditMode && editingWidget) {
      setTitle(editingWidget.title || '');
      setInitialTitle(editingWidget.title || '');
      setTagName(editingWidget.variable || '');
      setInitialTagNameValue(editingWidget.variable || '');
      setInstruction(editingWidget.description || '');
      setInitialInstruction(editingWidget.description || '');
      setSelectedVariables(Array.isArray(editingWidget.variables) ? editingWidget.variables : []);
      const nextUseTimeevents = editingWidget.use_timeevents;
      setUseTimeevents(nextUseTimeevents === undefined || nextUseTimeevents === null ? true : Boolean(nextUseTimeevents));
      const nextIsPlaceholder = editingWidget.is_placeholder;
      const resolvedPlaceholder = nextIsPlaceholder === undefined || nextIsPlaceholder === null ? false : Boolean(nextIsPlaceholder);
      setIsPlaceholder(resolvedPlaceholder);
      setInitialIsPlaceholder(resolvedPlaceholder);
      setIsVisible(parseVisibilityFlag(editingWidget.visible, !resolvedPlaceholder));
      const storedGlobalHeaderColor = normalizeHeaderColor(editingWidget?.header_color || editingWidget?.headerColor);
      const resolvedGlobalHeaderColor = storedGlobalHeaderColor || defaultHeaderColor;
      setGlobalHeaderColor(resolvedGlobalHeaderColor);
      setInitialGlobalHeaderColor(storedGlobalHeaderColor);
      setGlobalHeaderColorCleared(!storedGlobalHeaderColor);
      const storedHeaderColor = normalizeHeaderColor(editingWidget?.userPreferences?.settings?.headerColor);
      setHeaderColor(storedHeaderColor || resolvedGlobalHeaderColor);
      setInitialHeaderColor(storedHeaderColor);
      setHeaderColorCleared(!storedHeaderColor);
    } else if (!isEditMode) {
      // Reset form when not editing
      setTitle('');
      setInitialTitle('');
      setTagName('');
      setInitialTagNameValue('');
      setInstruction('');
      setInitialInstruction('');
      setSelectedVariables([]);
      setUseTimeevents(true);
      setIsPlaceholder(false);
      setInitialIsPlaceholder(false);
      setIsVisible(true);
      setGlobalHeaderColor(defaultHeaderColor);
      setInitialGlobalHeaderColor(null);
      setGlobalHeaderColorCleared(true);
      setHeaderColor(defaultHeaderColor);
      setInitialHeaderColor(null);
      setHeaderColorCleared(true);
    }
  }, [isEditMode, editingWidget, defaultHeaderColor]);

  useEffect(() => {
    if (!open || isEditMode) {
      return;
    }
    const normalizedTag = typeof initialTagName === 'string' ? initialTagName.trim() : '';
    if (!normalizedTag) {
      return;
    }
    setTagName(normalizedTag);
    setInitialTagNameValue(normalizedTag);
    setUserEditedTag(true);
  }, [open, isEditMode, initialTagName]);

  // Auto-generate tag name from title (only when adding, not editing)
  useEffect(() => {
    if (!isEditMode && title && !userEditedTag) {
      const autoTagName = title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
      setTagName(autoTagName);
    } else if (!isEditMode && !title) {
      setTagName('');
    }
  }, [title, isEditMode, userEditedTag]);

  // Load tags when modal opens
  useEffect(() => {
    if (open && ws && caseid) {
      sendMessage({ command: 'get_all_unique_tags', caseid });
    }
  }, [open, sendMessage, caseid]);

  // Handle getting all unique tags response
  const handleGetAllUniqueTags = useCallback((parsedMessage) => {
    const receivedTags = parsedMessage.data.tags || [];
    console.log('Received tags for widget modal:', receivedTags);
    setAvailableTags(receivedTags);
  }, []);

  const handleClose = useCallback(() => {
    setTitle('');
    setTagName('');
    setInstruction('');
    setSelectedVariables([]);
    setUseTimeevents(true);
    setIsPlaceholder(false);
    setInitialIsPlaceholder(false);
    setIsVisible(true);
    setLoading(false);
    setUserEditedTag(false);
    setEditingTagName(false);
    setGlobalHeaderColor(defaultHeaderColor);
    setInitialGlobalHeaderColor(null);
    setGlobalHeaderColorCleared(true);
    setHeaderColor(defaultHeaderColor);
    setInitialHeaderColor(null);
    setHeaderColorCleared(true);
    setInitialTitle('');
    setInitialTagNameValue('');
    setInitialInstruction('');
    setDescriptionChangeDialogOpen(false);
    setDiscardChangesDialogOpen(false);
    onClose();
  }, [defaultHeaderColor, onClose]);

  // Subscribe to WebSocket messages
  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (message.command === 'addWidget' || message.command === 'deleteWidget' || message.command === 'updateWidget') {
        setLoading(false);
        const shouldSkipClose = message.command === 'updateWidget' && skipCloseOnUpdateRef.current;
        if (shouldSkipClose) {
          skipCloseOnUpdateRef.current = false;
        }
        if (message.data.success && !shouldSkipClose) {
          handleClose();
        }
      } else if (message.command === 'get_all_unique_tags') {
        handleGetAllUniqueTags(message);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, handleGetAllUniqueTags, handleClose]);

  const normalizedTitle = title.trim();
  const normalizedInitialTitle = (initialTitle || '').trim();
  const normalizedVariable = tagName.trim();
  const normalizedInitialVariable = (initialTagNameValue || '').trim();
  const normalizedInstruction = (isPlaceholder ? '' : instruction).trim();
  const normalizedInitialInstruction = initialIsPlaceholder ? '' : (initialInstruction || '').trim();
  const hasUnsavedTitleChanges = normalizedTitle !== normalizedInitialTitle;
  const hasUnsavedTagChanges = normalizedVariable !== normalizedInitialVariable;
  const hasUnsavedDescriptionChanges = normalizedInstruction !== normalizedInitialInstruction;
  const hasUnsavedPlaceholderChanges = isPlaceholder !== initialIsPlaceholder;
  const hasUnsavedFormChanges = hasUnsavedDescriptionChanges || hasUnsavedTitleChanges || hasUnsavedTagChanges || hasUnsavedPlaceholderChanges;
  const hasDescriptionChanged = isEditMode && hasUnsavedDescriptionChanges;
  const handleCloseRequest = useCallback(() => {
    if (hasUnsavedFormChanges) {
      setDiscardChangesDialogOpen(true);
      return;
    }
    handleClose();
  }, [hasUnsavedFormChanges, handleClose]);
  const normalizedVariableLower = normalizedVariable.toLowerCase();
  const currentWidgetVariableLower = (editingWidget?.variable || '').trim().toLowerCase();
  const normalizedUseTimeevents = isPlaceholder ? false : useTimeevents;
  const isDuplicateTagInProject = availableTags.some((tag) => {
    const existingVariable = (tag?.variable || '').trim().toLowerCase();
    if (!existingVariable || existingVariable !== normalizedVariableLower) {
      return false;
    }
    if (isEditMode && existingVariable === currentWidgetVariableLower) {
      return false;
    }
    return true;
  });
  const duplicateTagMessage = t('widget.tag_already_used_in_project') || 'This tag name is already used in this project';

  const submitWidget = (skipRebuild = false) => {
    if (!title.trim() || !tagName.trim() || isDuplicateTagInProject) {
      return;
    }

    setLoading(true);

    const normalizedHeaderColor = normalizeHeaderColor(headerColor);
    const normalizedInitialHeaderColor = normalizeHeaderColor(initialHeaderColor);
    const shouldSaveHeaderColor = isEditMode
      && editingWidget
      && (headerColorCleared
        ? normalizedInitialHeaderColor !== null
        : normalizedHeaderColor !== normalizedInitialHeaderColor);
    const normalizedGlobalHeaderColor = normalizeHeaderColor(globalHeaderColor);
    const normalizedInitialGlobalHeaderColor = normalizeHeaderColor(initialGlobalHeaderColor);
    const shouldSaveGlobalHeaderColor = isEditMode
      && editingWidget
      && (globalHeaderColorCleared
        ? normalizedInitialGlobalHeaderColor !== null
        : normalizedGlobalHeaderColor !== normalizedInitialGlobalHeaderColor);
    
    if (isEditMode) {
      const updatePayload = {
        command: 'updateWidget',
        caseid: caseid,
        widget_id: editingWidget.id,
        title: normalizedTitle,
        variable: normalizedVariable,
        description: normalizedInstruction,
        variables: selectedVariables,
        use_timeevents: normalizedUseTimeevents,
        is_placeholder: isPlaceholder,
        visible: isVisible,
        skip_rebuild: skipRebuild
      };
      if (onWidgetOptimisticUpdate && editingWidget?.id) {
        onWidgetOptimisticUpdate(editingWidget.id, {
          title: normalizedTitle,
          variable: normalizedVariable,
          description: normalizedInstruction,
          use_timeevents: normalizedUseTimeevents,
          is_placeholder: isPlaceholder,
          visible: isVisible
        });
      }
      if (shouldSaveGlobalHeaderColor) {
        updatePayload.header_color = globalHeaderColorCleared ? null : (normalizedGlobalHeaderColor || defaultHeaderColor);
      }
      sendMessage(updatePayload);

      if (shouldSaveHeaderColor && sendMessage) {
        const existingSettings = (editingWidget?.userPreferences?.settings && typeof editingWidget.userPreferences.settings === 'object' && !Array.isArray(editingWidget.userPreferences.settings))
          ? editingWidget.userPreferences.settings
          : {};
        const nextSettings = { ...existingSettings };
        if (headerColorCleared) {
          nextSettings.headerColor = null;
        } else {
          nextSettings.headerColor = normalizedHeaderColor || defaultHeaderColor;
        }
        sendMessage({
          command: 'saveWidgetPreferences',
          caseid: caseid,
          widget_id: editingWidget.id,
          preferences: {
            ...nextSettings
          }
        });
      }
    } else {
      sendMessage({
        command: 'addWidget',
        caseid: caseid,
        title: normalizedTitle,
        variable: normalizedVariable,
        description: normalizedInstruction,
        variables: selectedVariables,
        use_timeevents: normalizedUseTimeevents,
        is_placeholder: isPlaceholder,
        visible: isVisible
      });
    }
  };

  const handleSubmit = () => {
    if (!title.trim() || !tagName.trim() || isDuplicateTagInProject) {
      return;
    }
    
    if (isEditMode && hasDescriptionChanged) {
        setDescriptionChangeDialogOpen(true);
        return;
    }
    
    submitWidget(false);
  };

  const handleDescriptionChangeClose = () => {
    setDescriptionChangeDialogOpen(false);
  };

  const handleDescriptionChangeConfirm = (skipRebuild) => {
    setDescriptionChangeDialogOpen(false);
    submitWidget(skipRebuild);
  };

  const handleDiscardChangesClose = () => {
    setDiscardChangesDialogOpen(false);
  };

  const handleDiscardChangesConfirm = () => {
    setDiscardChangesDialogOpen(false);
    handleClose();
  };

  const handleTagSelect = (variable) => {
    if (!selectedVariables.includes(variable)) {
      setSelectedVariables([...selectedVariables, variable]);
    }
    setTagsAnchorEl(null);
  };

  const handleRemoveVariable = (variableToRemove) => {
    setSelectedVariables(selectedVariables.filter(v => v !== variableToRemove));
  };

  const handleVariablesClick = (event) => {
    setTagsAnchorEl(event.currentTarget);
  };

  const handleDeleteWidget = () => {
    setDeleteDialogOpen(true);
  };

  const handleToggleVisibility = useCallback(() => {
    const nextVisibility = !isVisible;
    setIsVisible(nextVisibility);
    if (isEditMode && editingWidget?.id && sendMessage) {
      skipCloseOnUpdateRef.current = true;
      const updatePayload = {
        command: 'updateWidget',
        caseid,
        widget_id: editingWidget.id,
        visible: nextVisibility
      };
      sendMessage(updatePayload);
      if (onWidgetOptimisticUpdate && editingWidget?.id) {
        onWidgetOptimisticUpdate(editingWidget.id, { visible: nextVisibility });
      }
    }
  }, [caseid, editingWidget?.id, isEditMode, isVisible, onWidgetOptimisticUpdate, sendMessage]);

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    if (sendMessage && editingWidget) {
      sendMessage({
        command: 'deleteWidget',
        widget_id: editingWidget.id
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenDescriptionTagsMenu = (event) => {
    setDescriptionTagsAnchor(event.currentTarget);
  };

  const handleInsertTagInDescription = (tagName) => {
    // Focus into the TagEditor first to ensure insertion happens in the description
    if (instructionRef.current?.focus) {
      instructionRef.current.focus();
    }
    // Delay slightly to allow focus to settle (ensures selection/range is correct)
    setTimeout(() => {
      if (instructionRef.current?.insertTag) {
        instructionRef.current.insertTag(tagName);
      }
    }, 0);
    setDescriptionTagsAnchor(null);
  };

  const handleCopyTag = (tagName) => {
    try {
      navigator.clipboard.writeText(`{${tagName}}`);
    } catch (e) {
      console.error('Failed to copy tag', e);
    }
  };

  const handleOpenActionFlow = useCallback(() => {
    if (!caseid) return;
    const widgetParam = tagName ? `&widget=${encodeURIComponent(tagName)}` : '';
    navigate(`/case/${caseid}?openActions=true${widgetParam}`);
  }, [navigate, caseid, tagName]);

  // renderTextWithTagChips removed; TagEditor will still manage tag rendering in editor

  // Prepare additional tags for description (project-scope tags)
  const descriptionAdditionalTags = useMemo(() => [
    // Case Metadata
    { variable: 'projectid', description: t('widget.tag_project_id'), tag_type: 'system' },
    { variable: 'project_title', description: t('widget.tag_project_title'), tag_type: 'system' },
    { variable: 'project_description', description: t('widget.tag_project_description'), tag_type: 'system' },
    // System datetime tags
    { variable: 'currentyear', description: t('widget.tag_current_year'), tag_type: 'system' },
    { variable: 'currentmonth', description: t('widget.tag_current_month'), tag_type: 'system' },
    { variable: 'currentday', description: t('widget.tag_current_day'), tag_type: 'system' },
    { variable: 'currenthour', description: t('widget.tag_current_hour'), tag_type: 'system' },
    { variable: 'currentminute', description: t('widget.tag_current_minute'), tag_type: 'system' },
    { variable: 'currentsecond', description: t('widget.tag_current_second'), tag_type: 'system' }
  ], [t]);

  // Filter availableTags to only show widget tags (project-scope), not timeevent tags (event-scope)
  const widgetScopeTags = useMemo(() => {
    return availableTags.filter(tag => tag.tag_type === 'widget');
  }, [availableTags]);

  const instructionTags = useMemo(
    () => [...widgetScopeTags, ...descriptionAdditionalTags],
    [widgetScopeTags, descriptionAdditionalTags]
  );
  
  const isValid = title.trim() && tagName.trim() && !isDuplicateTagInProject;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseRequest}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(87, 87, 176, 0.12)',
            overflow: 'hidden',
            background: 'linear-gradient(to bottom, #ffffff, #fafafa)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 'none',
            background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.05rem',
            py: 1.6,
            px: 3,
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <AddIcon sx={{ color: '#fff' }} />
            <Typography variant="h5" noWrap sx={{ color: '#fff', fontWeight: 700, mr: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isEditMode ? t('widget.edit_widget') : t('widget.add_widget')}
            </Typography>
            {isEditMode && tagName && (
              <TagChip tagName={tagName} sx={{ ml: 1, fontSize: '0.8rem', padding: '1px 6px' }} />
            )}
          </Box>
          <CloseButton onClick={handleCloseRequest} sx={{ color: '#fff' }} />
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowY: 'auto', fontSize: '0.95rem', '& .MuiTypography-root': { fontSize: '0.95rem' }, '& .MuiFormControl-root': { fontSize: '0.95rem' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
            {/* Title + Inline Tag (responsive: stack on small screens) */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label={t('widget.question_field')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  required
                  variant="outlined"
                  size="medium"
                  placeholder={t('widget.title_placeholder')}
                  InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
                  InputProps={{
                    sx: {
                      '& .MuiInputBase-input': {
                        fontSize: '1.05rem',
                        padding: '10px 12px',
                        lineHeight: '1.25'
                      },
                      height: 48,
                      '&.Mui-focused': {
                        boxShadow: `0 6px 18px rgba(87,87,176,0.06)`
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: primaryColor
                      }
                    }
                  }}
                />
              </Box>

              <Box sx={{ width: { xs: '100%', sm: 220 } }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: { xs: 0.5, sm: 0 }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  {(!isEditMode && editingTagName) ? (
                    <TextField
                      size="small"
                      value={tagName}
                      onChange={(e) => {
                        const val = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9\s]/g, '')
                          .replace(/\s+/g, '_')
                          .replace(/_+/g, '_')
                          .replace(/^_|_$/g, '');
                        setTagName(val);
                        setUserEditedTag(true);
                      }}
                      onBlur={() => setEditingTagName(false)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <Chip
                      label={tagName || t('widget.tag_name_placeholder')}
                      clickable={!isEditMode}
                      onClick={!isEditMode ? () => setEditingTagName(true) : undefined}
                      size="medium"
                      sx={{ ...tagChipBaseStyle, fontWeight: 600 }}
                    />
                  )}
                  {!isEditMode && !editingTagName && (
                    <IconButton size="small" onClick={() => setEditingTagName(true)} aria-label={t('widget.edit_tag_name')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
            {isDuplicateTagInProject && (
              <Typography variant="body2" color="error" sx={{ mt: -2, fontWeight: 500 }}>
                {duplicateTagMessage}
              </Typography>
            )}

            {/* Instruction Field */}

            <Box sx={{ position: 'relative' }}>
              {!isPlaceholder ? (
                <>
                  <TagEditor
                    caseid={caseid}
                    label={t('widget.instruction')}
                    value={instruction}
                    onChange={(text) => setInstruction(text)}
                    placeholder={t('widget.instruction_placeholder')}
                    ref={instructionRef}
                    className="tag-editor"
                    onCopyTag={handleCopyTag}
                    disabled={false}
                    onTagClick={handleOpenDescriptionTagsMenu}
                    tags={instructionTags}
                  />
                </>
              ) : (
                <Box sx={{ p: 2, borderRadius: '8px', border: '1px dashed #ddd', textAlign: 'center', backgroundColor: 'rgba(250,250,250,0.6)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryColor, mb: 1 }}>
                    {t('widget.placeholder_label')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('widget.placeholder_instruction_note')}
                  </Typography>
                  <Tooltip title={isReadOnly ? t('widget.open_action_flow_readonly') : t('widget.open_action_flow')}>
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<WidgetsIcon />}
                        onClick={handleOpenActionFlow}
                        disabled={isReadOnly}
                        sx={{ backgroundColor: primaryColor, color: 'white', fontWeight: 600, borderRadius: '8px', px: 3, textTransform: 'none' }}
                      >
                        {t('widget.open_action_flow')}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {/* The above UI renders either the TagEditor for normal widgets or a placeholder CTA for placeholder widgets. */}
              
              {isPlaceholder && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem', fontStyle: 'italic' }}>
                  {t('widget.placeholder_instruction_note')}
                </Typography>
              )}
              {/* Description help is now in tooltip next to the 'Instruction' header */}
              {/* Preview removed per request */}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPlaceholder}
                  onChange={(e) => {
                    setIsPlaceholder(e.target.checked);
                    // When enabling placeholder, automatically disable useTimeevents
                    if (e.target.checked) {
                      setUseTimeevents(false);
                    }
                    if (!isEditMode) {
                      setIsVisible(!e.target.checked);
                    }
                  }}
                  sx={{
                    color: primaryColor,
                    '&.Mui-checked': { color: primaryColor }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('widget.is_placeholder_label')}
                  </Typography>
                  <Tooltip title={t('widget.is_placeholder_helper')}>
                    <IconButton
                      size="small"
                      sx={{ ml: 0.5 }}
                      onClick={() => setPlaceholderInfoOpen(true)}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{ alignItems: 'center' }}
            />

            {isEditMode && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: primaryColor, fontWeight: 600 }}>
                      {t('widget.global_header_color')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        type="color"
                        value={globalHeaderColor}
                        onChange={(e) => {
                          const nextColor = e.target.value;
                          setGlobalHeaderColor(nextColor);
                          setGlobalHeaderColorCleared(false);
                          if (headerColorCleared) {
                            setHeaderColor(nextColor);
                          }
                        }}
                        size="small"
                        sx={{
                          width: 120,
                          '& .MuiInputBase-input': {
                            height: 32,
                            padding: '4px 6px'
                          }
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setGlobalHeaderColor(defaultHeaderColor);
                          setGlobalHeaderColorCleared(true);
                          if (headerColorCleared) {
                            setHeaderColor(defaultHeaderColor);
                          }
                        }}
                        sx={{
                          borderRadius: '8px',
                          borderColor: 'rgba(0, 0, 0, 0.2)',
                          color: 'text.secondary',
                          textTransform: 'none'
                        }}
                      >
                        {t('widget.reset_header_color')}
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      {globalHeaderColorCleared
                        ? t('widget.global_header_color_cleared')
                        : t('widget.global_header_color_helper')}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: primaryColor, fontWeight: 600 }}>
                      {t('widget.personal_header_color')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        type="color"
                        value={headerColor}
                        onChange={(e) => {
                          setHeaderColor(e.target.value);
                          setHeaderColorCleared(false);
                        }}
                        size="small"
                        sx={{
                          width: 120,
                          '& .MuiInputBase-input': {
                            height: 32,
                            padding: '4px 6px'
                          }
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setHeaderColor(globalHeaderColor);
                          setHeaderColorCleared(true);
                        }}
                        sx={{
                          borderRadius: '8px',
                          borderColor: 'rgba(0, 0, 0, 0.2)',
                          color: 'text.secondary',
                          textTransform: 'none'
                        }}
                      >
                        {t('widget.use_global_header_color')}
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      {headerColorCleared
                        ? t('widget.header_color_cleared')
                        : t('widget.header_color_helper')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Variables Selection - Hidden for now */}
            {false && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: primaryColor, fontWeight: 600 }}>
                  {t('widget.variables')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('widget.variables_help')}
                </Typography>

                {/* Selected Variables */}
                {selectedVariables.length > 0 && (
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: 'rgba(87, 87, 176, 0.05)',
                      borderRadius: '12px',
                      border: `1px solid ${lightPurple}`
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1, color: primaryColor, fontWeight: 600 }}>
                      {t('widget.selected_variables')} ({selectedVariables.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedVariables.map((variable, index) => (
                        <Chip
                          key={index}
                          label={variable}
                          onDelete={() => handleRemoveVariable(variable)}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            color: '#4caf50',
                            '& .MuiChip-deleteIcon': {
                              color: '#4caf50',
                              '&:hover': {
                                color: '#388e3c'
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Add Variables Button */}
                <Button
                  variant="outlined"
                  onClick={handleVariablesClick}
                  fullWidth
                  sx={{
                    borderRadius: '12px',
                    border: `2px dashed ${primaryColor}`,
                    color: primaryColor,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: lightPurple,
                      border: `2px dashed ${hoverColor}`
                    }
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  {t('widget.add_variables')}
                </Button>

                {availableTags.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic' }}
                  >
                    {t('widget.no_custom_fields')}
                  </Typography>
                )}
                
                {/* Validation message for empty variables */}
                {selectedVariables.length === 0 && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mt: 2, textAlign: 'center', fontWeight: 500 }}
                  >
                    {t('widget.variables_required')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderTop: '1px solid rgba(0,0,0,0.06)',
            backgroundColor: '#fff',
            '& .MuiButton-root': { fontSize: '0.9rem' }
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={isVisible ? t('widget.hide_widget', 'Hide widget') : t('widget.show_widget', 'Show widget')} arrow>
                <IconButton
                  color="primary"
                  onClick={handleToggleVisibility}
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.08)'
                    }
                  }}
                  aria-label={isVisible ? t('widget.hide_widget', 'Hide widget') : t('widget.show_widget', 'Show widget')}
                >
                  {isVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {isEditMode && (
                <Tooltip title={t('delete')} arrow>
                  <span>
                    <IconButton
                      onClick={handleDeleteWidget}
                      color="error"
                      aria-label={t('delete')}
                      sx={{
                        borderRadius: '12px',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.06)'
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseRequest}
                variant="outlined"
              sx={{
                borderRadius: '12px',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isValid || loading || descriptionChangeDialogOpen || discardChangesDialogOpen}
              sx={{
                borderRadius: '12px',
                backgroundColor: primaryColor,
                '&:hover': {
                  backgroundColor: hoverColor
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)'
                }
              }}
              >
                {loading ? t('common.saving') : (isEditMode ? t('widget.update_widget') : t('widget.create_widget'))}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: '12px',
              backgroundColor: '#fff7d6',
              border: '1px solid #f2c94c'
            }}
          >
            <WarningAmberIcon sx={{ color: '#a66b00', mt: '2px' }} fontSize="small" />
            <Typography variant="caption" sx={{ color: '#6b4f00', lineHeight: 1.5 }}>
              {t('widget.ai_disclaimer')}
            </Typography>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={descriptionChangeDialogOpen}
        onClose={handleDescriptionChangeClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('widget.description_changed_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('widget.description_changed_body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleDescriptionChangeClose}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => handleDescriptionChangeConfirm(true)}
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            {t('widget.update_skip')}
          </Button>
          <Button
            onClick={() => handleDescriptionChangeConfirm(false)}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {t('widget.update_rebuild')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discardChangesDialogOpen}
        onClose={handleDiscardChangesClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('widget.cancel_change_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('widget.cancel_change_body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleDiscardChangesClose}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {t('widget.keep_editing')}
          </Button>
          <Button
            onClick={handleDiscardChangesConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            {t('widget.discard_changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Placeholder info dialog triggered by the info icon next to the checkbox */}
      <Dialog
        open={placeholderInfoOpen}
        onClose={() => setPlaceholderInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('widget.is_placeholder_label')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('widget.is_placeholder_helper')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setPlaceholderInfoOpen(false)}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Description Tags Dropdown */}
      <TagsDropdown
        tags={widgetScopeTags}
        handleTagSelect={handleInsertTagInDescription}
        anchorEl={descriptionTagsAnchor}
        setAnchorEl={setDescriptionTagsAnchor}
        additionalTags={descriptionAdditionalTags}
        scope="project"
      />

      {/* Tags Dropdown for Variables Selection */}
      <TagsDropdown
        tags={availableTags}
        handleTagSelect={handleTagSelect}
        anchorEl={tagsAnchorEl}
        setAnchorEl={setTagsAnchorEl}
        scope="project"
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `2px solid #d32f2f`,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#d32f2f', 
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningAmberIcon />
          {t('widget.delete_widget_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('widget.delete_widget_warning')}
          </DialogContentText>
          
          {/* Show widget details */}
          {editingWidget && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: 2,
              mb: 2
            }}>
              <Typography variant="h6" sx={{ color: '#d32f2f', mb: 1 }}>
                {editingWidget.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                <strong>{t('common.variable')}:</strong> {editingWidget.variable}
              </Typography>
              {editingWidget.description && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('common.description')}:</strong> {editingWidget.description}
                </Typography>
              )}
            </Box>
          )}
          
          <DialogContentText sx={{ color: '#d32f2f', fontStyle: 'italic' }}>
            {t('widget.delete_widget_note')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              color: '#666',
              borderColor: '#ddd',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#c62828' }
            }}
            startIcon={<DeleteIcon />}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddWidgetModal; 
